import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { usePurchaseOrderApiV1 } from '../../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { toast } from '../../../util/toasts';
import {
  PurchaseOrder,
  PurchaseOrderSearchRequest,
  PurchaseOrderStatus,
} from '../../../clients/quipli-service/purchase-order/purchase-order';
import { Typography } from '../../../components/typography/typography';
import { QuipliLink } from '../../../components/link/quipli-link';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { Badge } from '../../../components/badge/badge';
import { PurchaseOrderStatusTransitionButtons } from '../status-transition-button/purchase-order-status-transition-button';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { convertToMoney } from '../../../util/util';
import { Location } from '../../../clients/quipli-service/location/location';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Currency } from 'dinero.js';

export const PurchaseOrderList = () => {
  const { searchPurchaseOrders } = usePurchaseOrderApiV1();
  const { listLocations } = useAPI();

  const [isLoading, setLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const columns: DataTableColumn[] = [
    { label: 'PO Number', key: 'poNumber' },
    { label: 'Vendor', key: 'vendor' },
    { label: 'Tel. No.', key: 'phone' },
    { label: 'Total', key: 'total' },
    { label: 'Purchased At', key: 'purchasedAt' },
    { label: 'Purchased By', key: 'purchasedBy' },
    { label: 'Status', key: 'status' },
    { label: 'Actions', key: 'actions' },
    { label: '', key: 'fixedActions', fixedAsLast: true },
  ];

  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<PurchaseOrderSearchRequest>(
    {
      keyword: params.get('keyword') ?? '',
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    async () => {
      if (locations.length <= 0) return;

      setLoading(true);
      try {
        const { items, pagination } = await searchPurchaseOrders({
          keyword: request.keyword,
          pageSize: request.pageSize,
          pageNumber: request.pageNumber,
        });

        setData(items.map(buildDataRow));
        setPagination(pagination);
      } catch (e: any) {
        toast(e.message.msg ?? e.message ?? 'Failed to fetch purchase orders', 'error');
      } finally {
        setLoading(false);
      }
    },
    [locations],
  );

  const buildDataRow = (r: PurchaseOrder): DataTableRow => {
    const currency: Currency = (locations.find((l) => l.id === r.locationId)?.currency || 'USD') as Currency;
    return {
      mobileHeader: (
        <>
          <div>
            <Typography type="text-sm" className="text-quipli-black-800">
              <QuipliLink
                to={`purchase-orders/${r.id}`}
                className="underline underline-offset-1 text-[var(--quipli-black-800)]"
              >
                {`${r.poNumber} - ${r.vendor.name}`}
              </QuipliLink>
            </Typography>
          </div>
        </>
      ),
      fields: {
        poNumber: (
          <QuipliLink to={`purchase-orders/${r.id}`} className="underline underline-offset-1 text-quipli-black-800">
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {r.poNumber}
            </Typography>
          </QuipliLink>
        ),
        vendor: (
          <QuipliLink to={`vendors/${r.vendor.id}`} className="underline underline-offset-1 text-quipli-black-800">
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {r.vendor.name}
            </Typography>
          </QuipliLink>
        ),
        phone: (
          <Typography type="text-sm" className="truncate text-quipli-black-800">
            {r.vendor.phone || '-'}
          </Typography>
        ),
        total: (
          <div className="flex flex-col">
            <Typography type="text-sm" className="text-quipli-black-800">
              {convertToMoney(r.subtotal, currency).toFormat()}
            </Typography>
          </div>
        ),
        purchasedAt: (
          <>
            <Typography type="text-sm" className="text-quipli-black-800">
              {r.purchasedAt.toFormat('LLL-dd y')}
            </Typography>
          </>
        ),
        purchasedBy: (
          <Typography type="text-sm" className="text-quipli-black-800 capitalize">
            {r.purchasedBy.adminFullName}
          </Typography>
        ),
        status: (
          <Badge
            text={r.status}
            type={
              r.status === PurchaseOrderStatus.Cancelled
                ? 'danger'
                : r.status === PurchaseOrderStatus.Requested
                ? 'warning'
                : r.status === PurchaseOrderStatus.Approved
                ? 'success'
                : 'default'
            }
          />
        ),
        actions: <PurchaseOrderStatusTransitionButtons order={r} isDisabled={false} reload={fetchCallback} />,
        fixedActions: (
          <QuipliLink to={`purchase-orders/${r.id}`}>
            <Button className="icon-button">
              <Icon icon="Edit" />
            </Button>
          </QuipliLink>
        ),
      },
    };
  };

  useEffect(() => {
    listLocations().then((locations) => setLocations(locations));
  }, []);

  return (
    <div className="px-[10px] pb-[5px]">
      <Typography type="display-sm" weight="medium">
        Manage Purchase Orders
      </Typography>

      <div className="pt-5 flex items-center justify-between">
        <SearchBar placeholder="Search by keyword" defaultValue={request.keyword} setRequest={setRequest} />
        <Button className="button-yellow !border-0 h-[46px]" data-cy="create-order">
          <QuipliLink to="purchase-orders/create">
            <Typography
              type="text-md"
              weight="semi-bold"
              className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
            >
              Create
            </Typography>
          </QuipliLink>
        </Button>
      </div>

      <div className="pt-5">
        <DataTable
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
